import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Novara Future Capability — full learner loop (Paul's UAT build).
 * Registration -> AI interview -> FC assessment -> constellation
 * -> The Boundary (Behold Bakery, 41 cards) -> constellation -> Proving Grounds.
 *
 * The build ships as one self-contained static document that owns its own layout,
 * styles and state, so it is mounted in an iframe rather than inlined (its CSS is
 * global — body, *, .btn, .chip — and would collide with the app's).
 *
 * ADVISORY ONLY: the build scores client-side; results posted via onComplete are for
 * UAT review, never measurement. Serve /fc/index.html from the SAME origin as the app
 * so localStorage works (the Proving Grounds gate depends on it).
 *
 * Bridge protocol note: the build posts {source:"novara-fc", kind, payload}. Older
 * builds (v2) used `type`; we accept either so both flow. The messages the build can
 * send: boot (handshake), tel (telemetry), nav (screen), fc_item_answered,
 * fc_attempt_skip, and — on older builds — complete.
 *
 * Bridge health (KAN-247 scope 4): a build cannot assert its own completeness — an
 * export that lost the bridge would also lose the assertion, so nothing fires. The
 * real guard lives HERE: we expect a `boot` handshake within `bridgeTimeoutMs` of
 * iframe load and fail loudly if it never arrives, so a silent no-capture session
 * fails on the side that can actually detect it. `boot.payload.draw_source` also tells
 * us whether the run read a server attempt or the local fallback — surfaced via onBoot.
 */
export default function NovaraFC({
  src = "/fc/index.html",
  onTelemetry,
  onScreen,
  onComplete,
  onBoot,
  onBridgeFailure,
  onMessage,
  bridgeTimeoutMs = 8000,
  className = "",
  title = "Future Capability",
}) {
  const frameRef = useRef(null);
  const timerRef = useRef(null);
  const bootedRef = useRef(false);
  const [bridgeFailed, setBridgeFailed] = useState(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handler = useCallback(
    (event) => {
      const msg = event.data;
      if (!msg || msg.source !== "novara-fc") return;
      // Only trust messages from our own frame.
      if (frameRef.current && event.source !== frameRef.current.contentWindow) return;

      const kind = msg.kind || msg.type; // v4 uses `kind`; v2 used `type`
      const payload = msg.payload;

      if (kind === "boot") {
        // Handshake received — the bridge is live. Cancel the failure timer.
        bootedRef.current = true;
        clearTimer();
        if (bridgeFailed) setBridgeFailed(false);
        onBoot?.(payload);
      } else if (kind === "telemetry" || kind === "tel") {
        onTelemetry?.(payload);
      } else if (kind === "screen" || kind === "nav") {
        onScreen?.(payload);
      } else if (kind === "complete") {
        onComplete?.(payload);
      }
      // Nothing is silently dropped: forward every well-formed message.
      onMessage?.(kind, payload);
    },
    [onTelemetry, onScreen, onComplete, onBoot, onMessage, clearTimer, bridgeFailed]
  );

  useEffect(() => {
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [handler]);

  useEffect(() => clearTimer, [clearTimer]);

  // Start the failure timer when the iframe document loads. If the boot handshake
  // hasn't arrived by the deadline, the bridge is broken (in-frame, the build always
  // emits boot — even on the local fallback) — fail loudly rather than record nothing.
  const handleLoad = useCallback(() => {
    if (bootedRef.current) return;
    clearTimer();
    timerRef.current = setTimeout(() => {
      if (bootedRef.current) return;
      setBridgeFailed(true);
      // eslint-disable-next-line no-console
      console.error(
        "[NovaraFC] telemetry bridge handshake not received within " +
          `${bridgeTimeoutMs}ms — this session is not being recorded.`
      );
      onBridgeFailure?.();
    }, bridgeTimeoutMs);
  }, [bridgeTimeoutMs, onBridgeFailure, clearTimer]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100vh" }}>
      <iframe
        ref={frameRef}
        src={src}
        title={title}
        onLoad={handleLoad}
        className={className}
        style={{ width: "100%", height: "100%", border: 0, display: "block" }}
        // allow-same-origin is REQUIRED. Without it the frame gets an opaque origin,
        // localStorage silently no-ops, and the Proving Grounds gate loses
        // boundary_done_at — so the gap it exists to enforce can never be measured.
        sandbox="allow-scripts allow-same-origin allow-forms"
      />
      {bridgeFailed && (
        <div
          role="alert"
          data-testid="novarafc-bridge-failure"
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 99999,
            background: "#b91c1c",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            padding: 24,
            font: "700 18px/1.5 system-ui, sans-serif",
          }}
        >
          Telemetry bridge did not initialise — this session is not being recorded.
          Stop and report before continuing.
        </div>
      )}
    </div>
  );
}
